import API from "./api";

//
// Payment endpoints, wrapped so the pages do not repeat URL strings.
//
// There is deliberately no amount, tax or currency in any request below. The
// backend computes every figure from the stored plan and subscription and
// rejects a request that carries one by name, so a bug in the console cannot
// change what a customer is charged.
//

/**
 * Generate a payment link for a subscription and email it to the customer
 * @param {Object} options - The subscription to bill
 * @param {string} options.customerId - Customer UUID
 * @param {string} options.subscriptionId - Subscription UUID
 * @returns {Promise<Object>} The axios response
 */
export const generatePaymentLink = ({ customerId, subscriptionId }) =>
  API.post("/payments/payment-links", {
    customer_id: customerId,
    subscription_id: subscriptionId,
  });

/**
 * Fetch a page of payment history
 * @param {Object} params - Pagination, sorting and filter values
 * @returns {Promise<Object>} The axios response
 */
export const fetchPayments = (params) => API.get("/payments", { params });

/**
 * Fetch the totals and per-status counts for a filtered payment set
 * Takes the same filters as the list. Computed on the server over every matching row, not over the page the table happens to be showing -- a total that only covered page 1 would still read as the real one
 * @param {Object} params - The same filter values passed to fetchPayments
 * @returns {Promise<Object>} The axios response
 */
export const fetchPaymentSummary = (params) => API.get("/payments/summary", { params });

/**
 * Fetch one payment in full, including its billing snapshot
 * @param {string} paymentId - Payment UUID
 * @returns {Promise<Object>} The axios response
 */
export const fetchPayment = (paymentId) => API.get(`/payments/${paymentId}`);

/**
 * Resend the existing payment link. Never creates a new one
 * @param {string} paymentId - Payment UUID
 * @returns {Promise<Object>} The axios response
 */
export const resendPaymentLink = (paymentId) =>
  API.post(`/payments/${paymentId}/resend-payment-link`);

/**
 * Cancel an unpaid payment link
 * @param {string} paymentId - Payment UUID
 * @returns {Promise<Object>} The axios response
 */
export const cancelPaymentLink = (paymentId) =>
  API.post(`/payments/${paymentId}/cancel-payment-link`);

/**
 * Resend the receipt for a verified payment
 * @param {string} paymentId - Payment UUID
 * @returns {Promise<Object>} The axios response
 */
export const resendReceipt = (paymentId) => API.post(`/payments/${paymentId}/resend-receipt`);

/**
 * Download the pro-forma invoice PDF for a payment
 * Rendered on demand by the server from the payment's own billing_snapshot, so this is always the same document the customer was emailed -- never a stored copy that can drift from it
 * @param {string} paymentId - Payment UUID
 * @returns {Promise<Object>} The axios response, with `data` as a Blob
 */
export const downloadInvoice = (paymentId) =>
  API.get(`/payments/${paymentId}/invoice`, { responseType: "blob" });

/**
 * Ask whether the payment provider is configured on this environment
 * Lets the console hide the generate action on a deployment with no Razorpay credentials, rather than offering a button that can only fail. Returns a redacted summary; it never carries a key or a secret
 * @returns {Promise<Object>} The axios response
 */
export const fetchPaymentProviderStatus = () => API.get("/payments/provider-status");

/**
 * Pull a usable message out of an API error
 * The payment endpoints return a `reason` and, on a conflict, the record that blocked the operation; both are more use to the reader than a generic failure string
 * @param {Error} error - The axios error
 * @param {string} fallback - Message used when the response carries none
 * @returns {Object} { message, reason, payment }
 */
export const describeApiError = (error, fallback = "Something went wrong") => {
  const data = error?.response?.data;
  const validationErrors = data?.errors;

  return {
    message:
      (Array.isArray(validationErrors) && validationErrors.join(" ")) ||
      data?.message ||
      data?.error ||
      error?.message ||
      fallback,
    reason: data?.reason || null,
    payment: data?.data?.payment || null,
  };
};
